/**
 * Composable methods used with _Options API_
 *
 * @NOTE: Some anti-pattern usage inside, but even native `useTemplateRef`
 *        on <script setup> has concerns [1]
 */
import { compose, equals, head, Maybe, typeOf, when } from '~/utils/lib/core/prelude'
/**
 * @desc Legacy access of a Vue `this.$el`
 *
 *    __When?__ Only for VueComponent which has not update their standards for
 *    "vue3", ie. setup a dedicated ref to access the HTMLElement
 *
 *    /!\ use with caution, as it is not recommended by "vue3"
 *
 *    #### Caveat #1: eager declaration
 *    Contrary to `useTemplateRef` usage, this will return a function needing
 *    to be called eagerly when one need to access the $el/ref
 *
 *    #### Caveat #2: manual inference
 *    Contrary to `useTemplateRef`, this function is not interpolated with
 *    "vue-language-tools" plugin, therefore the typing of the resolved result
 *    has be manual infered
 *
 * @XXX "vue3" `$refs.xxx` polymorphism → can either be a single result
 *      or wrap within an <Array> (eg. when use dynamically `:ref=""`)
 *    ↳ Focusing on single result for now. This API is at the moment unpure
 *      in the sense that the `Maybe.Just` can return an pseudo either case
 *      (ie. `Element|VueInstance`). We do not want to add other smelly
 *      conditioning here (otherwise we could create a whole set of methods
 *      accordingly, with a rigid state of mind: "developer demand a single
 *      `Element` → `VueInstance` would reject, `Element[]` would reject")
 *
 * @example
 *      setup() {
 *        const getRootNode = useLegacyRootNode()
 *
 *        return { getRootNode }
 *      },
 *      created() {
 *        const $el = this.getRootNode<Element>() // Maybe.Nothing → not mounted yet
 *      },
 *      mounted() {
 *        const $el = this.getRootNode<Element>() // should be Maybe.Just
 *
 *        // map/chain(
 *        //  doSomething
 *        // )($el)
 *      },
 *
 * @link https://vuejs.org/api/component-instance.html#el
 */
export function useLegacyRootNode() {
  const instance = getCurrentInstance()
  /**
   * @this {import('@vueuse/core').VueInstance}
   * @template {Element | Element[] | Text} T
   * @return {import('~/utils/lib/core/prelude_').Maybe<T>}
   */
  return function() {
    try {
      // @ts-ignore: UNSAFE access
      const proxy = instance.vnode.el

      /// @XXX "vue3" `$refs.xxx` polymorphism
      ///    ↳ patch surgery: had the $ref is returned through an array, just
      ///      systematically unwrap it and the first one
      /// @link https://stackoverflow.com/a/56785428/6003907
      const proxy_ = when(
        compose(equals('array'), typeOf),
        head,
        // @ts-ignore: UNSAFE access
      )(proxy)

      return typeof proxy === 'undefined' ?
        Maybe.Nothing() :
        // @ts-ignore: compiler template limitation
        Maybe.Just(proxy)

    } catch (_) {
      return Maybe.Nothing()
    }
  }
}
/**
 * @desc Legacy access of a Vue via `this.$refs`
 *
 *    __When?__ Used with _Options API_ to mimick a Vue3 _Composition API_
 *    `useTemplateRef`, mainly for the case when `:ref` is declared with a
 *    dynamic key with a variable leading to infinite instanciation of `ref`
 *    (so in a way a combination for the "vue3" v-for ref example and the
 *    function ref exemple)
 *
 *    #### Caveat #1: eager declaration
 *    Contrary to `useTemplateRef` usage, this will return a function needing
 *    to be called eagerly when one need to access the ref
 *
 *    #### Caveat #2: manual inference
 *    Contrary to `useTemplateRef`, this function is not interpolated with
 *    "vue-language-tools" plugin, therefore the typing of the resolved result
 *    has be manual infered
 *
 * @example
 *      <div v-for="{ id } in collection">
 *        <div :ref="`my-ref-${id}`" />
 *      </div>
 *      setup() {
 *        const refSelector = useLegacyTemplateRef()
 *
 *        return { refSelector }
 *      },
 *      mounted() {
 *        const myRef = this.refSelector<...>('componentRefName')
 *        // <InstanceType<typeof MyComponent>>, or <HTMLInputElement>, etc
 *
 *        /// a) Narrowing or conditioning the `Maybe` monad
 *        ///
 *        if ( Maybe.isJust(myRef) ) {
 *
 *          const input = Maybe.fromJust( myRef )
 *          // do something with `input`
 *        }
 *
 *        /// b) Via Functional Programming pattern
 *        ///
 *        myRef
 *          .chain(instance => {
 *            // do something with `instance`
 *          })
 *      },
 *
 *
 * @source See more why `useTemplateRef`/`templateRef` are actual hypocrisy:
 * @link https://github.com/orgs/vuejs/discussions/11408
 * @link https://github.com/vuejs/core/blob/main/packages/runtime-core/src/helpers/useTemplateRef.ts
 */
export function useLegacyTemplateRef() {
  const instance = getCurrentInstance()
  /**
   * @param {string} refName
   * @template {Element | import('vue').VueElement | unknown} T
   * @return {import('~/utils/lib/core/prelude_').Maybe<T>}
   */
  return function (refName) {
    try {
      // @ts-ignore: UNSAFE access
      const proxy = instance.refs[refName]

      /// @XXX "vue3" wrapping $refs inside an `array` when inserting within an
      ///      HTML `v-for`
      ///    ↳ patch surgery: had the $ref is returned through an array, just
      ///      systematically unwrap it and the first one
      /// @link https://stackoverflow.com/a/56785428/6003907
      const proxy_ = when(
        compose(equals('array'), typeOf),
        head,
        // @ts-ignore: UNSAFE access
      )(proxy)

      // @XXX Vue3 not having simple predicate like Vue2 `myComp instanceof Vue`
      const isVueElement = proxy_ instanceof Object
      const isElement = proxy_ instanceof Element

      return isVueElement || isElement ?
        // @ts-ignore: compiler template limitation
        Maybe.Just( proxy_ ) :
        Maybe.Nothing()

    } catch (_) {
      return Maybe.Nothing()
    }
  }
}
